import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    changePassword as providerChangePassword,
    getAccessToken,
    getSessionUser,
    isConfigured,
    signOut as providerSignOut,
} from './provider';
import { setSessionExpiredHandler } from './donorApi';

/**
 * Donor session state.
 *
 * Kept entirely separate from the admin app's AuthContext and from its
 * localStorage keys (userInfo, token_validity, organization_logo). The two
 * portals share a browser and a domain but must not share session state: a
 * signed-in admin is not a signed-in donor, and signing out of one must not
 * touch the other.
 *
 * What this context is for: rendering. Whether a donor may see a particular
 * donation is decided by the server on every request. Nothing here is a
 * security control -- a donor who edited this state into believing they were
 * signed in would simply get 401s from the API.
 */

const DonorAuthContext = createContext(null);

export const DonorAuthProvider = ({ children }) => {
    const [status, setStatus] = useState('loading'); // loading | signedIn | signingOut | signedOut
    const [sessionUser, setSessionUser] = useState(null);

    const refreshSession = useCallback(async () => {
        if (!isConfigured()) {
            setStatus('signedOut');
            return false;
        }

        // Asks the provider for a token rather than trusting anything stored,
        // so a session that has expired while the tab sat open is detected on
        // the next navigation rather than on the next API call.
        const token = await getAccessToken();
        if (token) {
            setSessionUser(getSessionUser());
            setStatus('signedIn');
            return true;
        }

        setSessionUser(null);
        setStatus('signedOut');
        return false;
    }, []);

    // 'signingOut' rather than 'signedOut' until the browser leaves for
    // Cognito's /logout. 'signedOut' would make the route guard start an
    // automatic sign-in, whose redirect would cancel /logout -- and Cognito's
    // still-live session would sign the donor straight back in.
    const leaveVia = useCallback(async (providerCall) => {
        setStatus('signingOut');
        const leaving = await providerCall();
        if (!leaving) {
            setSessionUser(null);
            setStatus('signedOut');
        }
    }, []);

    const signOut = useCallback(() => leaveVia(providerSignOut), [leaveVia]);

    // Change password signs out first (Cognito will not reset a signed-in
    // session), so it goes through the same 'signingOut' state.
    const changePassword = useCallback(
        (returnTo) => leaveVia(() => providerChangePassword(returnTo)),
        [leaveVia],
    );

    useEffect(() => {
        refreshSession();
    }, [refreshSession]);

    useEffect(() => {
        // Lets donorApi tell us a session died mid-request, so the guard can
        // redirect through the router instead of a full page reload.
        setSessionExpiredHandler(() => {
            setSessionUser(null);
            // A request failing mid-sign-out (its tokens were just cleared) must
            // not flip us to 'signedOut' either; see signOut.
            setStatus((current) => (current === 'signingOut' ? current : 'signedOut'));
        });
        return () => setSessionExpiredHandler(null);
    }, []);

    const value = useMemo(
        () => ({
            status,
            isLoading: status === 'loading',
            isSignedIn: status === 'signedIn',
            isSigningOut: status === 'signingOut',
            sessionUser,
            configured: isConfigured(),
            refreshSession,
            signOut,
            changePassword,
        }),
        [status, sessionUser, refreshSession, signOut, changePassword],
    );

    return <DonorAuthContext.Provider value={value}>{children}</DonorAuthContext.Provider>;
};

export const useDonorAuth = () => {
    const context = useContext(DonorAuthContext);
    if (!context) {
        throw new Error('useDonorAuth must be used inside a DonorAuthProvider.');
    }
    return context;
};
